import React, { useState } from 'react';
import './gaEssay.css';
import { Say } from '../widgets/Say';
import { loadEnglishDictionaries } from '../../loader/loadEnglishDictionaries';
import { Say2 } from '../widgets/Say2';
import { LookupPronunciation } from './LookupPronunciation';
import { SampleLexicalSets } from './SampleLexicalSets';
import { SayRP } from './SayRP';
import { SpecificFormants } from './SpecificFormants';
import { Rule } from '../widgets/Rule';
import { RulesTable } from '../widgets/RulesTable';
import { PSlash, PSquare } from '../widgets/Delimit';

/** essay about going from dictionary pronunciation to a Western US accent */
export function WestUSEssay() {
  const [init, setInit] = useState(false);
  if (!init) {
    setInit(true);
    loadEnglishDictionaries();
  }

  return (<div className='page'>
    <h1>Making a Western US Accent</h1>

    <p>
      This essay discusses some of the quirks of a "Western US" accent,
      building up techniques needed to figure out how to pronounce different words in it.
      Specifically, I'm going to describe how to go from a generic dictionary pronunciation
      to the accent I use (Washington state), similar to other accents of English along the west coast of the US,
      which I'll abbreviate as <i>W-US</i> in this essay.
      While the details may differ, many of these techniques apply to other English accents and even other languages.
      To help give you useful context, you can click to hear text spoken when you
      see <Say>a triangle followed by underlined text</Say>.
    </p>

    <p>
      English is notorious for its difficult spelling,
      making it a challenge to figure out how to pronounce.
      For example, <i>ough</i> can be pronounced 6 different ways in the US:
      &nbsp;<Say>ought</Say>, <Say>bough</Say>, <Say>though</Say>, <Say>through</Say>, <Say>tough</Say>, and <Say>trough</Say>.
      And Britain adds a 7th: <SayRP>thorough</SayRP>&nbsp;
      (in the US, <Say>thorough</Say> rhymes with <Say>though</Say>).
    </p>

    <p>
      Of course, you can always look at a dictionary to see how it's pronounced, right?
      But it turns out that dictionary pronunciations are actually <i>approximations</i>,
      where the actual interpretation of the symbols varies by region, speaker, and context.
      To explain how we turn that dictionary approximation into a Western US accent, we'll cover <a href='#symbols'>symbols</a>
      , <a href='#vowels'>vowels</a>, <a href='#rhotics'>r-colored vowels</a>, <a href='#dark-l'>dark-l</a>
      , <a href='#lexical'>lexical sets</a>, <a href='#schwa'>schwa</a>, <a href='#aspiration'>aspiration</a>
      , <a href='#t'>transforming t</a>, and finish with a <a href='#dictionary'>Western US dictionary</a>.
    </p>

    <p>
      But before we get to a W-US accent, let's start with what a standard dictionary pronunciation tells us.
    </p>

    <h2 id='symbols'>Symbols</h2>

    <p>
      If you look at different English dictionaries,
      you'll see that they don't all use a single standard set of symbols for describing how to pronounce words.
      Typically, they try to balance using symbols that an English speaker might understand
      with the fact that English sounds don't align very well with its alphabet.
      We'll take a slightly different approach by using a dictionary
      like <a href="https://raw.githubusercontent.com/open-dict-data/ipa-dict/master/data/en_US.txt">this one</a>
      &nbsp;(warning: that dictionary is fairly large),
      which uses the <i>International Phonetic Alphabet</i> (IPA) for its pronunciations.
      It uses the following symbols for the various sounds,
      shown with a typical English spellings (or words) for some of the non-obvious symbols:
    </p>

    <ul>
      <li>vowels:&nbsp;&nbsp;
        <Say2>ɑ</Say2>, <Say2>æ</Say2>, <Say2>ɛ</Say2>, <Say2>i</Say2>, <Say2>ɪ</Say2>, <Say2>ɔ</Say2>, <Say2>u</Say2>, <Say2>ʊ</Say2>,&nbsp;
        <Say2>ə</Say2>, <Say2 alt='er'>ɝ</Say2>
      </li>
      <li>diphthongs:&nbsp;&nbsp;
        <Say2>aɪ̯</Say2>, <Say2>aʊ̯</Say2>, <Say2>eɪ̯</Say2>, <Say2>oʊ̯</Say2>, <Say2>ɔɪ̯</Say2>
      </li>
      <li>consonants from the alphabet:&nbsp;&nbsp;
        <Say2>b</Say2>, <Say2>d</Say2>, <Say2>f</Say2>, <Say2>ɡ</Say2>, <Say2>h</Say2>, <Say say='/ˈkʰaː/'>k</Say>,&nbsp;
        <Say2>m</Say2>, <Say2>n</Say2>, <Say say='/ˈpʰaː/'>p</Say>, <Say2>s</Say2>, <Say say='/ˈtʰaː/'>t</Say>,&nbsp;
        <Say2>v</Say2>, <Say2>w</Say2>, <Say2>z</Say2>,
      </li>
      <li>other consonants:&nbsp;&nbsp;
        <Say2 alt='y'>j</Say2>, <Say2 alt='l'>ɫ</Say2>, <Say2 alt='ng'>ŋ</Say2>, <Say2 alt='r'>ɹ</Say2>, <Say2 alt='thin'>θ</Say2>,&nbsp;
        <Say2 alt='this'>ð</Say2>, <Say2 alt='sh'>ʃ</Say2>, <Say2 alt='zh'>ʒ</Say2>, <Say2 alt='ch'>tʃ</Say2>, <Say2 alt='j'>dʒ</Say2>
      </li>
    </ul>

    <p>
      One of the first things you may notice is just how many different sounds there are,
      far more sounds than there are letters in the English alphabet!
      That's 10 vowels, 5 diphthongs, and 24 consonants, for a total of 39 sounds
      that have to get stuffed into the measly 5 vowel symbols and 21 consonant symbols of the alphabet.
      And then English even has to go and waste some letters - like <b>c</b>, <b>q</b>, and <b>x</b> - that
      simply repeat sounds that other letters already handle.
      This is just one of the many reasons why English spelling is so complex.
    </p>

    <p>Before we dig in to understanding a W-US accent, we'll introduce a few more symbols for pronunciations:</p>
    <ul>
      <li>
        Transcription:
        We'll put a / before and after the dictionary's approximate pronunciation,
        called a <i>broad transcription</i>, for example <Say>/ˈkɪti/</Say>.
        And when we refer to a specific pronuncation for a chosen accent, we'll put it in square brackets,
        called a <i>narrow transcription</i>, for example <Say say="[ˈkʰɪ3ᴅi]">[ˈkʰɪᴅi]</Say> (the new IPA symbols are explained below).
      </li>
      <li>
        Stress:
        To indicate where we put stress in a word, we'll use <b>ˈ</b> for the primary stress and <b>ˌ</b> for secondary stress.
        For example, <Say>/ˈɑktəˌpʊs/</Say> has primary stress on the first syllable, <Say>/ˈɑk/</Say>,
        and secondary stress on the final syllable, <Say>/ˌpʊs/</Say>,
        while the middle syllable, <Say>/tə/</Say>, is unstressed.
      </li>
      <li>
        Diphthongs:
        When a sound smoothly shifts from one vowel to another, it's called a <i>diphthong</i>.
        For example, the vowel in <Say say='/ˈpʰɹaɪ̯s/'>price</Say> starts with <Say>/a/</Say> and ends with <Say>/ɪ/</Say>.
        To indicate that these two vowels are pronounced as a single continuous sound rather than two separate sounds,
        we add a small mark under the second vowel like so: <Say>/aɪ̯/</Say>.
        Thus we have <Say say="/ˈpʰɹaɪ̯s/">/ˈpɹaɪ̯s/</Say> with a single smooth sound,
        rather than <Say say="/ˈpʰɹaɪs/">/ˈpɹaɪs/</Say> with two separate vowels.
        We count the diphthongs independently, separate from the single vowels, both because native speakers tend to think of them
        as a single sound and because there are several symbols that only appear as part of a diphthong
        but not by themselves: <Say2>a</Say2>, <Say2>e</Say2>, and <Say2>o</Say2>.
      </li>
    </ul>

    <p>
      Now that you've seen what the various symbols mean,
      you can use the following text box to look up the pronunciation of words in our US pronouncing dictionary.
      Keep in mind that this is an approximation, not a specific accent.
    </p>
    <LookupPronunciation />


    <h2 id='vowels'>Varying Vowels</h2>

    <p>
      One of the most obvious differences between accents is how vowels are pronounced.
      For example, compare <Say say='about'>about (W-US)</Say>
      , <Say say='/ɐˈbæʊ̯t/' textColor='rgb(141, 136, 31)'>about (Philadelphia)</Say>
      , <Say say='about' accent='New Dublin' textColor='rgb(190, 123, 15)'>about (Dublin)</Say>
      , <Say say='about' accent='Welsh (Cardiff)' textColor='rgb(197, 20, 20)'>about (Cardiff)</Say>
      , <Say say='about' accent='Scottish' textColor='rgb(31, 75, 141)'>about (Scottish)</Say>
      , and <Say say='about' accent='Glasgow' textColor='rgb(31, 75, 141)'>about (Glascow)</Say>.
      To a non-native speaker, some of those might be hard to distinguish (well, other than Glascow),
      but to someone emersed in an accent, those differences can suggest where the speaker is from
      or might even cause confusion if the pronunciation is too far from their expectations.
    </p>

    <p>
      One reason vowels can vary so much is that they can change along a continuum in multiple ways.
      To get an idea of this, start by saying <Say2>a</Say2> then slowly switch to transition to <Say2>i</Say2>,
      which might sound something like <Say2>aɪ̯ː</Say2>,
      traveling from <Say2>a</Say2> to <Say2>æ</Say2> to <Say2>ɛ</Say2> to <Say2>e</Say2> to <Say2>ɪ</Say2> to <Say2>i</Say2>
      , with lots of sounds in between.
    </p>

    <p>
      Intuitively, you can think of vowels as varying across several dimensions:
    </p>

    <ul>
      <li>
        With your mouth open – like <Say2>a</Say2> –
        in the middle – like <Say2>e</Say2> or <Say2>o</Say2> –
        or more closed – like <Say2>i</Say2> or <Say2>u</Say2>.
      </li>
      <li>
        With your tongue toward the front of your mouth – like <Say2>i</Say2> –
        more centrally – like <Say2>ə</Say2> – or the back – like <Say2>u</Say2>.
      </li>
      <li>
        With your lips rounded – like French <Say2>œ</Say2> –
        versus unrounded – like <Say2>ɛ</Say2>.
      </li>
      <li>
        With air escaping through your nose – like a nasal <Say2>ĩ</Say2> –
        versus non–nasal <Say2>i</Say2>.
      </li>
    </ul>

    <p>
      More precisely, linguists analyze speech sounds looking at the major frequencies of the sound waves,
      called <a href='https://en.wikipedia.org/wiki/Formant'>formants</a>.
      A convenient way to look at vowels is by graphing them using the two primary formants, F1 and F2,
      which roughly correspond to open/close and front/back vowels.
      Here's what the chart looks like for the vowel and diphthong approximations used by our US English dictionary:
    </p>

    <SpecificFormants which='US' />

    <p>
      But that's not quite how those vowels are pronounced in a Western US accent,
      which looks more like this:
    </p>

    <SpecificFormants which='W-US' />

    <p>
      To discuss those changes, we'll use the notation <Rule>a &gt; b</Rule> to indicate that the sound <Rule>a</Rule> changes into <Rule>b</Rule>.
      (For more information on the syntax used for sound changing rules, see <a href="?page=rules-essay">Sound Change Rules</a>.)
      Let's listen to what some of these changes sound like.
      Some of the differences may sound subtle, but to my ear, it sounds closer to my accent after the change.
    </p>

    <RulesTable rules={[
      { rule: "æ > ɛə̯", other: <>bat: <Say>/ˈbæt/</Say> versus <Say>/ˈbɛə̯t/</Say></> },
      {
        rule: "eɪ̯ > e̞i̯", other: <>rate: <Say>/ˈɹeɪ̯t/</Say> versus <Say>/ˈɹe̞i̯t/</Say>.
          The symbol on the bottom of <Say2>e̞</Say2> indicates that <Say2>e</Say2> should be pronounced "lowered".
        </>
      },
      {
        rule: "oʊ̯ > ɤ̞o̯", other: <>
          boat: <Say>/ˈboʊ̯t/</Say> versus <Say>/ˈbɤ̞o̯t/</Say>.
          "Rams horns", <Say2>ɤ</Say2>, is an IPA symbol for an "unrounded, close-mid, back" vowel,
          and <Say2>ɤ̞</Say2> is lowered.
        </>
      },
      {
        rule: "u > ɯu̯", other: <>
          boot: <Say>/ˈbut/</Say> versus <Say>/ˈbɯu̯t/</Say>.
          Turned-m, <Say2>ɯ</Say2>, is an IPA symbol for an "unrounded, close, back" vowel.
        </>
      },
      {
        rule: "ʊ > ʊ̞", other: <>
          foot: <Say>/ˈfʊt/</Say> versus <Say>/ˈfʊ̞t/</Say>.
          "Horseshoe", <Say2>ʊ</Say2>, is an IPA symbol for a "rounded, near-close, near-back" vowel and <Say2>ʊ̞</Say2> is lowered.
        </>
      },
    ]} />

    <p>
      There are a couple other interesting changes between those two charts.
      One is that <Say2>ɔ</Say2> disappeared, which we'll discuss in the Lexical sets section.
      The other is that <Say2>ə</Say2> has been replaced by <Say2>ɐ</Say2> and <Say2>ɨ</Say2>,
      which we'll talk about in the Schwa section.
    </p>


    <h2 id='rhotics'>R-colored vowels</h2>

    <p>
      North American accents are generally <i>rhotic</i>, meaning that they have "r-colored" vowels
      like <Say2>ɝ</Say2> and <Say2>ɪɹ</Say2> in words like <Say say='bird'>bird /ˈbɝd/</Say> and <Say say='near'>near /ˈnɪɹ/</Say>.
      This is in contrast with accents from much of England or Boston that are non-rhotic,
      which may instead sound like <SayRP>/ˈbɜːd/</SayRP> and <SayRP>/ˈnɪə̯/</SayRP>, without an r sound.
      (Note that <Say2>ɹ</Say2> is IPA for the sound at the beginning of <Say say="ˈɹɛd">red /ɹɛd/</Say>,
      while <Say2>r</Say2> is used for the trill in a language such as Spanish <SaySpan say='burro'>burro /ˈburo/</SaySpan>.)
    </p>

    <p>
      Our pronuncation dictionary contains the following
      rhotics: <Say2>ɝ</Say2>, <Say2>ɑɹ</Say2>, <Say2>ɛɹ</Say2>, <Say2>ɪɹ</Say2>, <Say2>ɔɹ</Say2>, and <Say2>ʊɹ</Say2>.
      The W-US versions are similar, except that /ɛɹ/ is closer to <Say2>e̞ɹ</Say2>,
      /ɔɹ/ is closer to <Say2>oɹ</Say2>, and /ʊɹ/ is closer to <Say2>ɤɹ</Say2>.
      You can hear these in <Say say='nurse'>nurse /ˈnɝs/</Say>, <Say say='start'>start /ˈstɑɹt/</Say>, <Say say='square'>square /ˈskwe̞ɹ/</Say>,
      <Say say='near'>near /ˈnɪɹ/</Say>, <Say say='force'>force /ˈfoɹs/</Say>, and <Say say='cure'>cure /ˈkʰjɤɹ/</Say>.
    </p>

    <p>
      The five rhotic combos behave much the same as other diphthongs, going smoothly from one vowel to another,
      but in this case, the second vowel is <Say2>ɝ</Say2>.
      For example, <Say2>ɪɹ</Say2> travels from <Say2>ɪ</Say2> to <Say2>ɝ</Say2>,
      so you could instead imagine it being represented as <Say say='/ɪɹ/'>ɪɝ̯</Say>.
      Note that <Say2>ɝ</Say2> is basically the rhotic version of the vowel <Say2>ɜ</Say2>,
      where the formants F1 and F2 are almost the same, but the third formant F3 is extra low.
    </p>

    <p>
      Below, the chart on the left shows the expected locations for the rhotics as they appear in the pronunciation
      dictionary: <Say2>ɝ</Say2>, <Say2>ɑɹ</Say2>, <Say2>ɛɹ</Say2>, <Say2>ɪɹ</Say2>, <Say2>ɔɹ</Say2>, and <Say2>ʊɹ</Say2>.
      The chart on the right shows the actual locations of the formants that the speech synthesizer uses for the corresponding W-US
      rhotics: <Say2>ɝ</Say2>, <Say2>ɑɹ</Say2>, <Say2>e̞ɹ</Say2>, <Say2>ɪɹ</Say2>, <Say2>oɹ</Say2>, and <Say2>ɤɹ</Say2>.
      For example, the left hand chart shows a line for <Say2>ʊɹ</Say2> that starts and ends in different places
      than the corresponding W-US <Say2>ɤɹ</Say2>.
    </p>

    <SpecificFormants which='US' rhotics={true} />
    <SpecificFormants which='W-US' rhotics={true} />

    <p>
      I find it interesting that, when people talk about diphthongs in American English,
      they generally don't include the rhotics even though they behave like other diphthongs.
      (...though there are phonetic English alphabets such
      as <a href="https://en.wikipedia.org/wiki/Quikscript">Quikscript</a> that
      include separate characters for both the standard diphthongs and the rhotics.)
      For our purposes, I'll consider the rhotics as diphthongs just like other blended vowels.
    </p>


    <h2 id='dark-l'>Dark-l</h2>

    <p>
      You may have wondered why our pronouncing dictionary uses <b>ɫ</b> rather
      than a plain old <i>l</i> symbol for the l-sound in words like <i>tail</i>.
      This is the symbol for "dark-l", an l-sound where you raise the back of your tongue when pronouncing it,
      which sounds like <Say say="/ɫ̩ː/">/ɫ/</Say>.
      In contrast, the regular <Say say="/l̩ː/">/l/</Say> is used for "light-l",
      where the tip or blade of the tongue touches the roof of the mouth just behind the teeth.
      Dark-l <Say say="/ɫ̩ː/">/ɫ/</Say> has a similar quality as a vowel (like the r-flavored <Say2>ɝ</Say2>)
      and can impact vowels that come immediately before it,
      especially back vowels like <Say2>i</Say2>, <Say2>o</Say2>, and <Say2>u</Say2>.
    </p>

    <ul>
      <li>
        <b>i:</b> You can hear the difference between the two l's in a word like <i>feel</i> or <i>fooled</i>:
        consider the light-l versions <Say say="/ˈfi200l/">feel /ˈfil/</Say> and <Say say="/ˈfu200ld/">fooled /ˈfuld/</Say> versus
        the dark-l versions <Say say="[ˈfiɫ]">feel /ˈfiɫ/</Say> and <Say say="[ˈfuɫd]">fooled /ˈfuɫd/</Say>.
      </li>
      <li>
        <b>o:</b> The Western US version of <Say2>o</Say2> is the <Say2>ɤ̞o̯</Say2> we saw in <Say>boat</Say>.
        But when it occurs before <i>l</i>, it sounds more like <Say2>o</Say2>.
        In <i>bowl</i> for example, it sounds more like <Say>/ˈboɫ/</Say> than <Say>/ˈbɤ̞o̯l/</Say>.
      </li>
      <li>
        <b>u:</b> The Western US version of <Say2>u</Say2> is the <Say2>ɯu̯</Say2> we saw in <Say>boot</Say>.
        But when it occurs before <i>l</i>, it sounds more like <Say2>u</Say2>.
        In <i>jewel</i> for example, it sounds more like <Say>/ˈdʒuɫ/</Say> than <Say>/ˈdʒɯu̯l/</Say>.
      </li>
    </ul>

    <p>
      Note that the pronouncing dictionary uses /ɫ/ for all l-sounds,
      though some may be closer to /l/ such as <Say>lamp</Say> or <Say>feeling</Say>.
    </p>


    <h2 id='lexical'>Lexical sets</h2>

    <p>
      When I first looked through the pronouncing dictionary, I was surprised to see
      that <Say say='/ˈɫɑt/'>lot /ˈɫɑt/</Say> and <Say say='/ˈθɔt/'>thought /ˈθɔt/</Say>&nbsp;
      are listed with different vowels, even though they rhyme when I say them: <Say>lot</Say> and <Say>thought</Say>,
      both spoken with <Say2>ɑ</Say2>.
      This is because <Say2>ɔ</Say2> has merged with <Say2>ɑ</Say2> in my accent,
      while some other accents still maintain the original distinction.
    </p>

    <p>
      If we're trying to figure out how to turn what's in the pronouncing dictionary into a W-US accent,
      our initial guess might be that any time the pronouncing dictionary says <Say>/ɔ/</Say>, we should turn it into <Say>[ɑ]</Say>.
      But that can't be true since <Say say='force'>force /ˈfɔɹs/</Say> definitely shouldn't be pronounced <Say>[ˈfɑɹs]</Say>.
      So how do we know when to pronounce it as-is and when to change it?
    </p>

    <p>
      It turns out that words with /ɔɹ/ are pronounced <Say>/oɹ/</Say>,
      while other instances of <Say>/ɔ/</Say> usually change to <Say>[ɑ]</Say>.
      In more general terms, we can talk about <a href='https://en.wikipedia.org/wiki/Lexical_set'>lexical sets</a>,
      sets of English words that typically change together from accent to accent.
      Lot, thought, and force are all representatives of <i>different</i> lexical sets.
      In W-US, the <Say>lot</Say> and <Say>thought</Say> lexical sets are pronounced <Say>[ɑ]</Say>&nbsp;
      while the <Say>force</Say> lexical set is pronounced <Say>[oɹ]</Say>.
      In contrast, in a British Recieved Pronunciation (RP) accent,
      &nbsp;<SayRP>lot</SayRP> uses <SayRP>[ɒ]</SayRP>,
      while &nbsp;<SayRP>thought</SayRP> and <SayRP>force</SayRP> both use <SayRP>[ɔː]</SayRP>,
      where the ː symbol that looks like a colon (but isn't) indicates that the vowel is held longer.
      Here are a few more examples from these lexical sets:
    </p>

    <SampleLexicalSets />

    <p>
      One way to describe when to change <Say>/ɔ/</Say> is to specify the <i>context</i> in which it should change.
      We'll give one rule that says to change it to <PSlash>o</PSlash> when followed by <PSlash>ɹ</PSlash>,
      and a second rule that changes it to <Say>[ɑ]</Say> when the first rule doesn't apply.
      To indicate that the rule only applies based on the surrounding context,
      we follow the rule with <Rule>/</Rule> plus the appropriate context.
      This is what these two rules look like:
    </p>

    <RulesTable rules={[
      { rule: "ɔ > o / _ɹ", other: <>change to /o/ if followed by /ɹ/</> },
      { rule: "ɔ > ɑ", other: <>if the first rule doesn't apply, use this rule instead</> },
    ]} />


    <h2 id='schwa'>Schwa</h2>

    <p>
      So far, all our rules for turning the vowels from our pronouncing dictionary into Western US vowels
      have been fairly straight-forward:
      one vowel or diphthong always changes to another one, or else it changes in one specific case.
    </p>

    <p>
      Schwa, <Say2>ə</Say2>, in contrast, turns into <i>lots</i> of different sounds depending on the surrounding context.
      In IPA, schwa is a mid-central vowel, spoken with a more relaxed mouth than most other vowels.
      In our pronunciation dictionary, it's used to indicate a variety of neutral or even non-existent vowel sounds,
      sometimes merging with adjacent sounds.
      (Note that some discussions of English use the term <i>schwa</i> only for an unstressed vowel,
      using a different symbol for the vowel in <Say>but</Say> such as <Say2>ʌ</Say2>.
      Our pronouncing dictionary uses schwa to refer to a general mid-central vowel regardless of stress.)
    </p>

    <div className='subheader'>case 1: syllabic consonants</div>

    <p>
      We're taught that some letters are vowels and others are consonants, a nice, easy, and precise separation.
      But, it turns out that it's not quite so cut and dried.
    </p>

    <p>
      Consider <Say>bird</Say>. What's the vowel in the middle?
      Some dictionaries will list the pronunciation as <PSlash>ˈbərd</PSlash> or <PSlash>ˈbəɹd</PSlash>,
      but someone with a W-US accent doesn't actually say <Say2>ə</Say2> followed by an r sound.
      They instead use a single sound that behaves like a vowel.
      Our pronunciation dictionary represents this sound as <Say2>ɝ</Say2>,
      hence its entry for bird is <Say>/ˈbɝd/</Say>.
      An alternate transciption for <PSlash>ɝ</PSlash> is <PSlash>ɹ̩</PSlash>,
      where the small line under the r means that it's syllabic;
      in other words, it behaves like a vowel rather than a consonant.
    </p>

    <p>
      But <b>r</b> isn't the only English consonant that can behave like a vowel:&nbsp;
      <b>l</b>, <b>m</b>, and <b>n</b> all can as well.
      In my accent, a schwa before those letters often completely disappears.
      For example, while some accents pronounce <i>wouldn't</i> as <Say>/ˈwʊdənt/</Say>,
      where you hear the schwa in the middle of the final syllable, my accent says <Say say="/ˈwʊ̞dn̩t/">[ˈwʊ̞dn̩t]</Say>.
      The second syllable is <Say>[dn̩t]</Say>, where the "vowel" in the middle is simply <Say>[n̩]</Say>.
    </p>

    <p>
      Here are some more examples contrasting the dictionary pronuncation versus W-US:
    </p>

    <ul>
      <li>final: <Say say='/ˈfaɪ̯nəˌɫ/'>/ˈfaɪ̯nəɫ/</Say> versus <Say>[ˈfaɪ̯nɫ̩]</Say></li>
      <li>people: <Say say='/ˈpipəˌɫ/'>/ˈpipəɫ/</Say> versus <Say>[ˈpipɫ̩]</Say></li>
      <li>system: <Say>/ˈsɪstəm/</Say> versus <Say>[ˈsɪstm̩]</Say></li>
      <li>maximum: <Say>/ˈmæksəməm/</Say> versus <Say>[ˈmɛə̯ksm̩m̩]</Say></li>
      <li>distance: <Say>/ˈdɪstəns/</Say> versus <Say>[ˈdɪstn̩s]</Say></li>
      <li>Edmonton: <Say>/ˈɛdməntən/</Say> versus <Say>[ˈɛdmn̩tn̩]</Say></li>
    </ul>

    <p>
      So, does this mean we have the rules <Rule>əɫ &gt; ɫ̩</Rule>, <Rule>əm &gt; m̩</Rule>, and <Rule>ən &gt; n̩</Rule>?
      Nope, it's not that simple. Consider the following:
    </p>

    <ul>
      <li>ton: <Say say='ton'>/ˈtən/</Say>, not <Say>/ˈtn̩/</Say></li>
      <li>unmet: <Say>/ənˈmɛt/</Say>, not <Say>/n̩ˈmɛt/</Say></li>
      <li>bunny: <Say>/ˈbəni/</Say>, not <Say>/ˈbn̩i/</Say></li>
      <li>amalgam: <Say>/əˈmɛə̯ɫɡm̩/</Say>, not <Say>/m̩ˈɛə̯ɫɡm̩/</Say></li>
    </ul>

    <p>
      These examples show cases where we <i>shouldn't</i> collapse the schwa:
      at the start of a word, in a stressed syllable, or when the lmn starts a stressed syllable.
      When we look at all the ways <Say2>t</Say2> can change in a later section,
      we'll see that it's tied to some of the schwa rules as well.
    </p>

    <p>
      Roughly, this gives us the following rules (where ! means <i>not</i> and # indicates a word boundary):
    </p>

    <RulesTable rules={[
      { rule: "əɫ > ɫ̩ / !#_ 'U'", other: <>not at start of word in unstressed syllable</> },
      { rule: "əm > m̩ / !#_ 'U'", other: <>not at start of word in unstressed syllable</> },
      { rule: "ən > n̩ / !#_ 'U'", other: <>not at start of word in unstressed syllable</> },
      { rule: "əɹ > ɝ / !#_ 'U'", other: <>not at start of word in unstressed syllable (or <PSlash>ɹ̩</PSlash>)</> },
    ]} />
    <br />

    <div className='subheader'>case 2: stressed versus unstressed</div>

    <p>
      Schwa also appears in the pronuncation of words beyond just the syllabic consonants.
      And it turns out that W-US doesn't quite pronounce it <Say2>ə</Say2>.
      It typically ends up as either <Say2>ɐ</Say2> or <Say2>ɨ</Say2> depending
      on whether the syllable it's in and the surrounding syllables are stressed or unstressed.
      For example, <Say>/ˈbəs/</Say> turns into <Say>[ˈbɐs]</Say> not <Say>[ˈbɨs]</Say>,
      while <Say>/ˈfoʊ̯kəs/</Say> turns into <Say>[ˈfɤ̞o̯kɨs]</Say> not <Say>[ˈfɤ̞o̯kɐs]</Say>.
      Here are the cases:
    </p>

    <ul>
      <li>
        <Say2>ɐ</Say2> at the start of end of a word:
        for example, <Say say='about'>about ['ɐˈbaʊ̯t]</Say> and <Say say='arena'>arena [ɐˈɹinɐ]</Say>
      </li>
      <li>
        <Say2>ɐ</Say2> in a stressed syllable:
        for example, <Say say='bus'>bus [ˈbɐs]</Say> and <Say say='bunny'>bunny [ˈbɐni]</Say>
      </li>
      <li>
        <Say2>ɐ</Say2> in an unstressed syllable surrounded by stressed syllables:
        for example, <Say say='apocalyptic'>apocalyptic ['ɐˌpɑkɐˈɫɪpt̪ɪk]</Say> and <Say say='apolitical'>apolitical [ˌe̞i̯pɐˈɫɪᴅɪkɫ̩]</Say>
      </li>
      <li>
        <Say2>ɨ</Say2> otherwise:
        for example, <Say say='ballad'>ballad ['ˈbɛə̯ɫɨd]</Say> and <Say say='abracadabra'>abracadabra [ˌɛə̯bɹɨkɨˈdɛə̯bɹɐ]</Say>
      </li>
    </ul>

    <p>
      Or summarized as rules:
    </p>

    <RulesTable rules={[
      { rule: "ə > ɐ / #_ or _#", other: <>start or end of word</> },
      { rule: "ə > ɐ / 'S'", other: <>in stressed syllable</> },
      { rule: "ə > ɐ / 'S_U_S'", other: <>if syllable is unstressed while previous and next syllables are stressed</> },
      { rule: "ə > ɨ", other: <>otherwise</> },
    ]} />

    <p>
      It's also interesting to note that sometimes rules like these can help us figure out the proper dictionary pronunciation.
      For example, I was surprised to hear it convert <i>subterranean</i> to <Say>[sɨbtɝˈe̞i̯nin̩]</Say>&nbsp;
      when I actually pronounce it as <Say>[sɐbtɝˈe̞i̯nin̩]</Say>.
      When I looked in the pronouncing dictionary, it was listed as <Say say='/səbtɝˈe̞i̯niən/'>/səbtɝˈeɪniən/</Say>,
      and the rules tell us to convert that initial /ə/ to /ɨ/.
      This pointed out to me that the first syllable actually has secondary stress.
      Once I fixed that, <Say say='/ˌsəbtɝˈe̞i̯niən/'>/ˌsəbtɝˈeɪniən/</Say> properly turns into <Say say='subterranean'>[ˌsɐbtɝˈe̞i̯nin̩]</Say>.
    </p>

    <p>
      We've seen <Say2>ə</Say2> turn into <Say2>ɫ̩</Say2>, <Say2>m̩</Say2>
      , <Say2>n̩</Say2>, <Say2>ɝ</Say2>, <Say2>ɐ</Say2>, or <Say2>ɨ</Say2>,
      depending on where it occurs in the word, the stress on various syllables, and what consonants follow it.
      That's a lot of roles for a single symbol.
    </p>


    <h2 id='aspiration'>Aspiration</h2>

    <p>
      In some situations in English, <Say2>k</Say2>, <Say2>p</Say2>, and <Say2>t</Say2> are followed
      by a slight puff of air before a vowel, which causes a slight pause before the vowel starts.
      In particular, they're aspirated before a vowel when at the start of a word or stressed syllable.
      This puff of air is called <i>aspiration</i> and is marked with ʰ.
      Contrast <Say2>k</Say2> and <Say2>kʰ</Say2>, <Say2>p</Say2> and <Say2>pʰ</Say2>, <Say2>t</Say2> and <Say2>tʰ</Say2>.
    </p>

    <p>
      Examples:
    </p>

    <ul>
      <li>Aspirated at start of word: key: <Say>[ˈkʰi]</Say> not <Say>[ˈki]</Say>,
        pea: <Say>[ˈpʰi]</Say> not <Say>[ˈpi]</Say>, and tea <Say>[ˈtʰi]</Say> not <Say>[ˈti]</Say>.</li>
      <li>Aspirated at start of stressed syllable: <Say say='cacao'>cacao [kʰɨˈkʰaʊ̯]</Say>
        , <Say say='applaud'>applaud [ɐˈpʰɫɑd]</Say>, <Say say='titanic'>titanic [tʰaɪ̯ˈtʰɛə̯nɪk]</Say></li>
      <li>Unaspirated after /s/: <Say say='ski'>ski [ˈski]</Say>
        , <Say say='speed'>speed [ˈspid]</Say>, or <Say say='steed'>steed [ˈstid]</Say>.</li>
      <li>Unaspirated in unstressed syllable: <Say say='lucky'>lucky [ˈɫɐki]</Say>
        , <Say say='guppy'>guppy [ˈɡɐpi]</Say>. <PSlash>t</PSlash> is a special case we'll consider next.</li>
    </ul>

    <p>
      In English, the only way unaspirated <PSlash>k</PSlash>, <PSlash>p</PSlash>, or <PSlash>t</PSlash> can be before a vowel in a syllable
      but <i>not</i> at the start of a syllable is if there's an /s/ in front of it.
      Thus these cases can all be summarized with the following rules:
    </p>

    <RulesTable rules={[
      { rule: "k > kʰ / #_", other: <>aspirated at start of word</> },
      { rule: "k > kʰ / {!s}_ 'S'", other: <>aspirated in a stressed syllable that doesn't start with <PSlash>s</PSlash></> },
      { rule: "p > pʰ / #_", other: <>aspirated at start of word</> },
      { rule: "p > pʰ / {!s}_ 'S'", other: <>aspirated in a stressed syllable that doesn't start with <PSlash>s</PSlash></> },
      { rule: "t > tʰ / #_", other: <>aspirated at start of word</> },
      { rule: "t > tʰ / {!s}_ 'S'", other: <>aspirated in a stressed syllable that doesn't start with <PSlash>s</PSlash></> },
    ]} />

    <p>
      Differences in aspiration are often interpreted as giving somebody an accent,
      whether it's an English speaker using aspiration for an unaspirated sound in a different language,
      or a speaker of a language with unaspirated sounds not aspirating their English.
    </p>


    <h2 id='t'>Transforming <PSlash>t</PSlash></h2>

    <p>
      Like the schwa, <PSlash>t</PSlash> ends up transforming into a surprising variety of different sounds depending on context.
      Something that speakers of a language think of as a unique sound that can be used to differentiate words is called a <i>phoneme</i>,
      while the various ways that sound might be pronounced depending on context are called <i>allophones</i>.
      The allophones of the <Say2>t</Say2> phoneme that we'll explore
      are <Say2>t</Say2>, <Say2>tʰ</Say2>, <Say2>ᴅ</Say2>, and <Say2>ʔ</Say2>.
    </p>

    <div className='subheader'>case 1: flaps</div>

    <p>
      In words like <Say say='/ˈwɪtʰi/'>witty /ˈwɪti/</Say>, the <Say2>t</Say2> ends up changing to more of a <Say2>d</Say2>,
      making <i>witty</i> sound like <Say>widdy</Say>.
      Linguists call this sound a <i>voiced alveolar flap</i> or <i>tap</i>.
      This is similar to, but quicker than, an unaspirated <PSlash>t</PSlash> - <Say>/ˈwɪti/</Say>.
    </p>

    <p>
      [<i>A note on notation:</i>&nbsp;
      Linguists typically use <Say2>ɾ</Say2> for this flap, a phoneme that appears in roughly a quarter of the languages
      on <a href='https://phoible.org/'>PHOIBLE</a>, a repo of phonological linguistics data.
      Spanish, for example, uses this phoneme in words such as <SaySpan say='peɾo'>pero /ˈpeɾo/</SaySpan>.
      Interestingly, eSpeak defines two different flaps - one used for languages like Spanish,
      and another for accents of English that use a flap - and they're subtlety different.
      I'll use <Say2>ɾ</Say2> for the Spanish flap and, to borrow some notation
      from <a href='https://en.wikipedia.org/wiki/Americanist_phonetic_notation'>Americanist notation</a>,
      I'll use <Say2>ᴅ</Say2> for the English flap.
      While subtle, it sounds to me like <PSlash>ɾ</PSlash> is closer to the Spanish flap,
      for example <SaySpan say='poro'>/ˈpoɾo/</SaySpan> is slightly better
      than <SaySpan say='poᴅo'>/ˈpoᴅo/</SaySpan>, while <PSlash>ᴅ</PSlash> is closer to the W-US flap,
      for example <Say say="/ˈwɪᴅi/">witty /ˈwɪᴅi/</Say> is better than <Say say="/ˈwɪɾi/">witty /ˈwɪɾi/</Say>.
      Since I want both sounds, I need two symbols.
      And since I think of <Say2>ɾ</Say2> as having an r-ish quality and <Say2>ᴅ</Say2> as having a d-ish quality,
      I'll use <PSquare>ᴅ</PSquare> for the W-US flap.
      Interestingly, <a href='https://phoible.org/parameters/6289FA51CE1564F60633C2D1E19EE574#2/6.0/72.6'>several languages on
        PHOIBLE</a> use <PSlash>ᴅ</PSlash> for their tap.]
    </p>

    <p>
      <Say2>t</Say2> turns to <Say2>ᴅ</Say2> in unstressed syllables between any vowel
      and a weak vowel (like <PSlash>ə</PSlash>) or syllabic consonant (other than n).
      This gives us <Say say='witty'>witty [ˈwɪᴅi]</Say>, <Say say='auto'>auto [ˈɑᴅɤ̞o̯]</Say>
      , <Say say='butter'>butter [ˈbɐᴅɝ]</Say>, <Say say='metal'>metal [ˈmɛᴅɫ̩]</Say>
      , and <Say say='bottom'>bottom [ˈbɑᴅm̩]</Say>.
      Similarly, it changes in an unstressed syllable preceded by an r or syllabic-n and followed by a vowel, r, or syllabic (other than n);
      for example <Say say='aorta'>aorta [e̞i̯ˈoɹᴅɐ]</Say>, <Say say='smarter'>smarter [ˈsmɑɹᴅɝ]</Say>
      , <Say say='seventy'>seventy [ˈsɛvn̩ᴅi]</Say>, and <Say say='artemis'>artemis [ˈɑɹᴅm̩ɨs]</Say>.
    </p>

    <p>
      But it stays as <PSquare>t</PSquare> or <PSquare>tʰ</PSquare> in stressed syllables, for example&nbsp;
      <Say say='retail'>retail [ˈɹiˌtʰe̞i̯əɫ]</Say> and <Say say='fourteen'>fourteen [ˈfoɹˈtʰin]</Say>.
      It also stays as <PSquare>t</PSquare> after most consonants, for example&nbsp;
      <Say say='malted'>malted [ˈmɑɫtʰɨd]</Say>, <Say say='mastiff'>mastiff [ˈmɛə̯stɨf]</Say>,
      and <Say say='advantage'>advantage [ɐdˈvɛə̯ntʰɪdʒ]</Say>.
      And it stays as t before stronger vowels, for example&nbsp;
      <Say say='teton'>teton [ˈtʰiˌtʰɑn]</Say>, <Say say='detail'>detail [ˈditʰe̞i̯əɫ]</Say>, and <Say say='detour'>detour [ˈditʰʊ̞ɝ]</Say>.
    </p>

    <p>
      Sheesh. That sounds pretty complicated, doesn't it?
      Well, obviously speakers of W-US don't actually think about those rules,
      so it's only complex when we try to reason through going from a standardized pronunciation to a specific W-US accent.
    </p>

    <p>
      Here's one way to write those rules, capturing which vowels cause a change and which don't:
    </p>

    <RulesTable rules={[
      {
        rule: "t > ᴅ / V_(ə|ɪ|i|ɤ̞o̯|ɝ|ɹ|ɫ̩|m̩) 'U'",
        other: <>in an unstressed syllable,
          t changes to ᴅ when preceded by a vowel and followed by a weak vowel or most syllabic consonants</>
      },
      {
        rule: "t > ᴅ / (ɝ|ɹ|n̩ )_(V|ɝ|ɹ|ɫ̩|m̩) 'U'",
        other: <>in an unstressed syllable, t changes to ᴅ when preceded by ɝ, ɹ, or n̩,
          and followed by a vowel, ɝ, ɹ, ɫ̩, or m̩</>
      },
    ]} />

    <p>
      Lest you get the impression that this neatly covers all cases where W-US flaps a <PSlash>t</PSlash>,
      I'll point out that the <a href="https://en.wikipedia.org/wiki/Flapping">Wikipedia article on flapping</a>&nbsp;
      says, "The exact conditions for flapping in North American English are unknown..."
      It points out that a flap is added in compound words joined by vowel_t_vowel,
      for example <Say say='shutout'>shutout [ˈʃɐˌᴅaʊ̯t]</Say> or <Say say='whatever'>whatever [ˌhwɐˈᴅɛvɝ]</Say>.
      And it <i>doesn't</i> add a flap when the <PSlash>t</PSlash> is "foot-initial",
      or the beginning of a "morphological chunk" within the word,
      for example <Say say='mediterranean'>Mediterranean [ˌmɛdɨtʰɝˈe̞i̯nin̩]</Say> or <Say say='returnee'>returnee [ɹɪtʰɝˈni]</Say>.
      And then there's <Say say='90'>90 [ˈnaɪ̯nᴅi]</Say>, which doesn't follow any of those rules.
      My tools don't know how to auto-detect these cases, so they're instead listed as exceptions.
    </p>

    <div className='subheader'>case 2: glottal stop</div>

    <p>
      The final sound we'll look at is <PSquare>ʔ</PSquare>, the <i>glottal stop</i>.
      It's the sound you hear before the vowels in <Say say='uh-oh'>uh-oh [ˈʔɐˌʔɤ̞o̯]</Say>.
      It's also the sound you hear in the W-US pronunciation of <Say>button</Say> instead of a <PSlash>t</PSlash>.
      The W-US pronunciation is transcribed as <Say>[ˈbɐʔn̩]</Say>, several steps away from the dictionary's <Say>/ˈbətən/</Say>.
      Another entertaining word is <Say say='mountain'>mountain [ˈmaʊ̯ʔn̩]</Say> instead of <Say>/ˈmaʊ̯ntən/</Say>,
      where <i>nt</i> turns into ʔ (with the n's nasal sound transferring to the preceding vowel) and the final few letters get swallowed into n̩.
    </p>

    <p>
      It may look like final <Say>/tən/</Say> always turns into <Say>[ʔn̩]</Say>,
      but we can find words where it instead turns into <Say>[tʰn̩]</Say>, for example&nbsp;
      <Say say='edmonton'>edmonton [ˈɛdmn̩tʰn̩]</Say>, <Say say='overton'>overton [ˈɤ̞o̯vɝtʰn̩]</Say>, and <Say say='atherton'>atherton [ˈɛə̯θɝtʰn̩]</Say>.
      Why those?
      Maybe it's because they're "foot-initial" like we saw earlier?
      Or maybe it's because the final two syllables are unstressed, but the syllable before that is stressed?
      Well, the examples I have follow the second pattern, which is something my tools can detect,
      so I'll go with that until I find counter-examples.
    </p>

    <p>
      This gives us several rules that generally apply to the final syllable of a word:
    </p>

    <RulesTable rules={[
      {
        rule: "tən > tʰn̩ / 'SU_U'",
        other: <>current syllable is unstressed, previous is unstressed, syllable before previous is stressed.<br />
          for example, edmonton <Say>/ˈɛdməntən/</Say> to <Say>[ˈɛdmn̩tʰn̩]</Say></>
      },
      {
        rule: "ntən > ʔn̩ / '_S_U'",
        other: <>the syllable with /n/ is stressed, the syllable with /tən/ is unstressed.<br />
          for example, mountain <Say>/ˈmaʊ̯ntən/</Say> to <Say>[ˈmaʊ̯ʔn̩]</Say></>
      },
      {
        rule: "tən > ʔn̩ / (V|ɹ)_ 'U'",
        other: <>current syllable is unstressed.<br />
          for example, button <Say>/ˈbətən/</Say> to <Say>[ˈbɐʔn̩]</Say></>
      },
      {
        rule: "tnɪŋ > ʔnɪŋ / (V|ɹ)_ '_S_U'",
        other: <>the syllable with /t/ is stressed, the syllable with /nɪŋ/ is unstressed.<br />
          for example, lightning <Say>/ˈɫaɪ̯tnɪŋ/</Say> to <Say>[ˈɫaɪ̯ʔnɪŋ]</Say></>
      },
    ]} />
    <br />

    <div className='subheader'>case 3: aspiration</div>

    <p>
      As we saw in the section on <a href='#aspiration'>aspiration</a>, <Say2>t</Say2> turns into
      aspirated <Say2>tʰ</Say2> at the start of a word or stressed syllable.
      And as we just saw in this section, it oftens weakens into <Say2>ᴅ</Say2> or <Say2>ʔ</Say2> in unstressed syllables.
    </p>

    <p>
      But if you were paying close attention to the discussions of flaps and the glottal stop,
      you may have noticed <PSlash>tʰ</PSlash> appeared in several words where we would have expected
      an unaspirated <PSlash>t</PSlash> because they were in unstressed syllables and not at the start of the word.
      Why? Listen to the contrast in these words:
      detour <Say>[ˈditʰʊɹ]</Say> versus <Say>[ˈditʊɹ]</Say>,
      Mediterranean <Say>[ˌmɛdɨtʰɝˈe̞i̯nin̩]</Say> versus <Say>[ˌmɛdɨtɝˈe̞i̯nin̩]</Say>,
      and edmonton <Say>[ˈɛdmn̩tʰn̩]</Say> versus <Say>[ˈɛdmn̩tn̩]</Say>.
      The unaspirated <PSlash>t</PSlash> sounds a lot like <PSlash>d</PSlash> or <PSlash>ᴅ</PSlash>,
      but it seems like the same forces that are keeping these instances sounding
      like <PSlash>t</PSlash> also make the sound strong enough that it's aspirated.
    </p>


    <h2 id='dictionary'>Western US dictionary</h2>

    <p>
      OK, we now have a whole collection of rules – tweaking vowels, figuring out the schwa, adding aspiration, and transforming t –
      which take us from the generic US dictionary pronunciation to a Western US accent.
      Let's see what the final tally of symbols looks like for our transcription of W-US:
    </p>

    <ul>
      <li>vowels:&nbsp;&nbsp;
        <Say2>ɑ</Say2>, <Say2>ɛ</Say2>, <Say2>i</Say2>, <Say2>ɪ</Say2>, <Say2>ʊ̞</Say2>,&nbsp;
        <Say2>ɐ</Say2>, <Say2>ɨ</Say2>, <Say2>ɝ</Say2>
      </li>
      <li>diphthongs:&nbsp;&nbsp;
        <Say2>aɪ̯</Say2>, <Say2>aʊ̯</Say2>, <Say2>e̞i̯</Say2>, <Say2>ɛə̯</Say2>, <Say2>ɔɪ̯</Say2>, <Say2>ɤ̞o̯</Say2>, <Say2>ɯu̯</Say2>
      </li>
      <li>r- and l-combos:&nbsp;&nbsp;
        <Say2>ɑɹ</Say2>, <Say2>e̞ɹ</Say2>, <Say2>ɪɹ</Say2>, <Say2>oɹ</Say2>, <Say2>ɤɹ</Say2>
        , <Say2>oɫ</Say2>, <Say2>uɫ</Say2>
      </li>
      <li>syllabic consonants:&nbsp;&nbsp;
        <Say2>ɫ̩</Say2>, <Say2>m̩</Say2>, <Say2>n̩</Say2>
      </li>
      <li>consonants:&nbsp;&nbsp;
        <Say2>b</Say2>, <Say2>d</Say2>, <Say2>f</Say2>, <Say2>ɡ</Say2>, <Say2>h</Say2>, <Say2>k</Say2>, <Say2>kʰ</Say2>,&nbsp;
        <Say2>m</Say2>, <Say2>n</Say2>, <Say2>p</Say2>, <Say2>pʰ</Say2> <Say2>s</Say2>, <Say2>t</Say2>, <Say2>tʰ</Say2>,&nbsp;
        <Say2>v</Say2>, <Say2>w</Say2>, <Say2>z</Say2>,&nbsp;
        <Say2 alt='y'>j</Say2>, <Say2 alt='l'>ɫ</Say2>, <Say2 alt='ng'>ŋ</Say2>, <Say2 alt='r'>ɹ</Say2>, <Say2 alt='thin'>θ</Say2>,&nbsp;
        <Say2 alt='this'>ð</Say2>, <Say2 alt='sh'>ʃ</Say2>, <Say2 alt='zh'>ʒ</Say2>, <Say2 alt='ch'>tʃ</Say2>, <Say2 alt='j'>dʒ</Say2>,&nbsp;
        <Say2>ᴅ</Say2>, <Say2>ʔ</Say2>
      </li>
    </ul>

    <p>
      That's 25 vowels (including diphthongs, rhotics, l-combos, and syllabic consonants) and 29 consonants for a total of 54 sounds
      needed to describe how to pronounce English with a Western US accent.&nbsp;
      <Say2>ɔ</Say2> merged with <Say2>ɑ</Say2>
      ; we added the rhotic diphthongs; added a couple <Say2>ɫ</Say2> combos; <Say2>ə</Say2> split into <Say2>ɐ</Say2> and <Say2>ɨ</Say2>&nbsp;
      then united with several consonants to make <Say2>ɫ̩</Say2>, <Say2>m̩</Say2>, <Say2>n̩</Say2>
      ; we have aspirated <Say2>kʰ</Say2>, <Say2>pʰ</Say2>, and <Say2>tʰ</Say2>&nbsp;
      ; and <Say2>t</Say2> sprouted the additional sounds <Say2>ᴅ</Say2> and <Say2>ʔ</Say2>.
    </p>

    <p>
      There are lots more details, depending on just how precise you want to get,
      but this is sufficient for our speech synthesizer,
      taking us from the stilted dictionary pronunciation:<br />
      &nbsp;&nbsp;&nbsp;<Say say='[ˈaɪ̯ ˈwʊdənt ˈdu ˈme̞i̯ntənəns əv ˈmɛtəɫ]'>I wouldn't do maintenance of metal /ˈaɪ̯ ˈwʊdənt ˈdu ˈme̞i̯ntənəns əv ˈmɛtəɫ/</Say><br />
      ...to something that sounds closer to a Western US accent:<br />
      &nbsp;&nbsp;&nbsp;<Say say="I wouldn't do maintenance of metal">I wouldn't do maintenance of metal [ˈaɪ̯ ˈwʊ̞dn̩t ˈdɯu̯ ˈme̞i̯ʔn̩n̩s ɐv ˈmɛᴅɫ̩]</Say>.
    </p>

    <p>
      In the box below, you can type in English words and see both the generic US pronunciation and the W-US version of the word.
      Note that the W-US pronunciation includes dots between the syllables,
      since it needed to figure out where the syllables were in order to adjust the pronunciation.
    </p>

    <LookupPronunciation includeWUS={true} />

    <hr />

  </div>);
}

export function SaySpan(props: { say?: string, children: string }) {
  return (<Say language='Spanish' accent='Mexico' say={props.say} textColor='rgb(150, 60, 170)'>
    {props.children}
  </Say>);
}
